import { useFlags } from '@atlaskit/flag';
import { Unsubscribe } from 'firebase/auth';
import { limit, QueryConstraint, QueryDocumentSnapshot, QuerySnapshot, startAfter } from 'firebase/firestore';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { showErrorFlag } from 'core/utilities/flags-helper';
import { ISharedTableRow } from 'shared/components/table/table-interface';

interface IUseCollectionProps {
  apiService: any; // TODO: Add type
  constraints?: QueryConstraint[];
  initialLimit?: number;
  loadMoreLimit?: number;
}

export const useCollection = <T extends { uid: string }>({
  apiService,
  constraints,
  initialLimit = 20,
  loadMoreLimit = 20,
}: IUseCollectionProps) => {
  const flags = useFlags();
  const constraintsRef = useRef<QueryConstraint[] | undefined>(constraints);
  const previousConstraintsString = useRef<string>('');

  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [data, setData] = useState<T[] | null>(null);
  const [tableRows, setTableRows] = useState<ISharedTableRow<T>[]>([]);
  const [lastDoc, setLastDoc] = useState<QueryDocumentSnapshot<T> | null>(null);
  const [hasMore, setHasMore] = useState(true);

  const constraintsString = useMemo(() => {
    if (!constraints || constraints.length === 0) return '';
    return JSON.stringify(constraints);
  }, [constraints]);

  const stableConstraints = useMemo(() => {
    if (constraintsString !== previousConstraintsString.current) {
      previousConstraintsString.current = constraintsString;
      constraintsRef.current = constraints;
    }
    return constraintsRef.current;
  }, [constraintsString, constraints]);

  const initialConstraints = useMemo(
    () => [...(stableConstraints || []), limit(initialLimit)],
    [stableConstraints, initialLimit],
  );

  useEffect(() => {
    const handleSnapshot = async (querySnapshot: QuerySnapshot<T>) => {
      const docs = querySnapshot.docs;
      const data = docs.map((doc) => doc.data());

      setData(data as T[]);
      setLastDoc(docs[docs.length - 1] || null);
      setHasMore(docs.length === initialLimit);
      setLoading(false);
    };

    /**
     * A subscription that failed, said out loud.
     *
     * The flag stays vague because the person reading it cannot act on a
     * Firestore code — but somebody has to be able to. A missing composite
     * index is permanent and needs a developer, and it arrives here looking
     * exactly like a dropped connection: "please try again" sent us hunting for
     * twenty minutes on a screen that could never have worked.
     *
     * `failed-precondition` carries the console link that creates the index, so
     * it is repeated rather than summarised.
     */
    const handleSubscriptionError = (error: any) => {
      // eslint-disable-next-line no-console
      console.error('[useCollection] subscription failed', {
        code: error?.code,
        message: error?.message,
        constraints: initialConstraints,
      });

      const permanent = error?.code === 'failed-precondition' || error?.code === 'permission-denied';
      showErrorFlag(
        'An error occurred',
        permanent
          ? 'This list could not be loaded. It is a configuration problem rather than a temporary one — the details are in the browser console.'
          : 'Could not retrieve data, please try again.',
        flags,
      );
      setLoading(false);
    };

    let unsubscribe: Unsubscribe;
    setLoading(true);

    unsubscribe = apiService.onCollectionSnapshot(handleSnapshot, handleSubscriptionError, initialConstraints);

    return () => {
      unsubscribe();
    };
  }, [apiService, flags, initialConstraints, initialLimit]);

  const loadMore = useCallback(async () => {
    if (!lastDoc || loadingMore || !hasMore) return;

    setLoadingMore(true);

    try {
      const loadMoreConstraints = [...(constraints || []), startAfter(lastDoc), limit(loadMoreLimit)];

      const querySnapshot = await apiService.getAll(loadMoreConstraints);
      const docs = querySnapshot.docs;
      const newData = docs.map((doc: any) => doc.data()) as T[];

      if (newData.length > 0) {
        setData((prevData) => [...(prevData || []), ...newData]);
        setLastDoc(docs[docs.length - 1]);
        setHasMore(docs.length === loadMoreLimit); // If we got exactly the limit, there might be more
      } else {
        setHasMore(false);
      }
    } catch (error) {
      showErrorFlag('An error occurred', 'Could not load more items, please try again.', flags);
    } finally {
      setLoadingMore(false);
    }
  }, [lastDoc, loadingMore, hasMore, constraints, loadMoreLimit, apiService, flags]);

  useEffect(() => {
    if (data) {
      setTableRows(
        data.map((item) => ({
          key: item.uid,
          data: item,
        })),
      );
    }
  }, [data]);

  return { data, loading, tableRows, loadMore, hasMore, loadingMore };
};
